import { BlocRepliable } from './BlocRepliable';
import { HANDICAPS, libelleHandicap } from '../lib/handicap';
import type { Handicap } from '../types';

export interface SaisieHandicap {
  handicap: Handicap | null;
  precision: string;
}

/**
 * Situation de handicap dans un formulaire d'adherent.
 *
 * C'est une donnee de SANTE — sensible au sens de l'article 9 du RGPD.
 * Trois consequences dans ce composant :
 *   - elle se DECLARE, elle ne se deduit pas : « Aucune » est le defaut,
 *     et rien ne l'infere depuis une activite ou un tarif ;
 *   - elle se RETIRE : repasser a « Aucune » efface aussi la precision,
 *     sans quoi une donnee de sante survivrait a son retrait ;
 *   - la precision sert l'ACCUEIL (« PAI en cours », « salle
 *     accessible »), pas le diagnostic. L'aide le dit a la saisie.
 */
export function ChampHandicap({
  valeur,
  onChanger,
}: {
  valeur: SaisieHandicap;
  onChanger: (valeur: SaisieHandicap) => void;
}) {
  // Replie par defaut (2026-10-13) : la ligne de titre dit la situation,
  // le bloc ne s'ouvre que pour la changer.
  const resume = valeur.handicap
    ? `${libelleHandicap(valeur.handicap)}${valeur.precision ? ` — ${valeur.precision}` : ''}`
    : 'aucune situation déclarée';

  return (
    <BlocRepliable titre="Handicap" resume={resume}>
      <label className="block">
        <span className="text-xs text-encre-500">Situation déclarée</span>
        <select
          className="champ mt-1"
          value={valeur.handicap ?? ''}
          onChange={(evenement) => {
            const code = (evenement.target.value || null) as Handicap | null;
            // Retirer la situation efface la precision : elle n'a plus
            // rien a preciser, et la base la refuserait orpheline.
            onChanger({ handicap: code, precision: code ? valeur.precision : '' });
          }}
        >
          <option value="">Aucune</option>
          {HANDICAPS.map((candidat) => (
            <option key={candidat.code} value={candidat.code}>
              {candidat.libelle}
            </option>
          ))}
        </select>
      </label>

      {valeur.handicap && (
        <label className="mt-3 block">
          <span className="text-xs text-encre-500">Précision</span>
          <input
            className="champ mt-1"
            value={valeur.precision}
            onChange={(evenement) => onChanger({ ...valeur, precision: evenement.target.value })}
            placeholder="PAI en cours, salle accessible, accompagnant…"
            maxLength={200}
          />
        </label>
      )}

      <p className="mt-2 text-xs text-encre-400">
        Donnée de santé : ne renseigner que ce qui est utile à l’accueil et à l’enseignement.
        Repasser à « Aucune » efface aussi la précision.
      </p>
    </BlocRepliable>
  );
}
